import { add, addDays, Duration } from 'date-fns';

import {
  ScheduledBankTransferEndDateDeterminedBy,
  ScheduledBankTransferFrequency,
} from '__generated__/GQL';
import { formatDate } from 'helpers/date';

import { endDateDeterminedByLocales } from './commonLocales';

export const MAXIMUM_TRANSFER_AMOUNT_EUROS = 10000;
export const newTransferNeedsReceipt = (amount?: number) =>
  typeof amount === 'number' ? amount >= MAXIMUM_TRANSFER_AMOUNT_EUROS : false;

enum NeverOption {
  Never = 'NEVER',
}

export type TScheduledBankTransferEndDateDeterminedByOption =
  | ScheduledBankTransferEndDateDeterminedBy
  | NeverOption;
export const ScheduledBankTransferEndDateDeterminedByOption = {
  ...ScheduledBankTransferEndDateDeterminedBy,
  ...NeverOption,
};

export const getTomorrowDate = () => addDays(new Date(), 1);

const FREQUENCY_MAPPING = {
  [ScheduledBankTransferFrequency.EveryMonth]: 'months',
  [ScheduledBankTransferFrequency.EveryWeek]: 'weeks',
  [ScheduledBankTransferFrequency.EveryYear]: 'years',
} as const satisfies {
  [key in Exclude<
    ScheduledBankTransferFrequency,
    ScheduledBankTransferFrequency.Once
  >]: keyof Duration;
};

export interface ComputeLastDueDateParams {
  nextDueDate: string;
  numberOfOccurrences: number;
  frequency: Exclude<
    ScheduledBankTransferFrequency,
    ScheduledBankTransferFrequency.Once
  >;
}
export const computeLastDueDate = ({
  frequency,
  nextDueDate,
  numberOfOccurrences = 1,
}: ComputeLastDueDateParams): Date =>
  add(new Date(nextDueDate), {
    [FREQUENCY_MAPPING[frequency]]: numberOfOccurrences - 1,
  });

export interface GetLastDueDateForDisplayParams {
  endDateDeterminedBy: TScheduledBankTransferEndDateDeterminedByOption;
  nextDueDate: string;
  numberOfOccurrences?: number | null;
  lastDueDate?: string | null;
  frequency: ScheduledBankTransferFrequency;
}

export const getLastDueDateForDisplay = ({
  endDateDeterminedBy,
  frequency,
  lastDueDate,
  nextDueDate,
  numberOfOccurrences = 1,
}: GetLastDueDateForDisplayParams): string => {
  if (frequency === ScheduledBankTransferFrequency.Once) {
    return '';
  }
  switch (endDateDeterminedBy) {
    case ScheduledBankTransferEndDateDeterminedByOption.Never:
      return endDateDeterminedByLocales[
        ScheduledBankTransferEndDateDeterminedByOption.Never
      ];
    case ScheduledBankTransferEndDateDeterminedByOption.ExplicitDate:
      return lastDueDate ? formatDate(new Date(lastDueDate), 'P') : '';
    case ScheduledBankTransferEndDateDeterminedByOption.NumberOfOccurrences:
      return numberOfOccurrences
        ? formatDate(
            computeLastDueDate({ frequency, nextDueDate, numberOfOccurrences }),
            'P',
          )
        : '';
    default:
      return '';
  }
};
